import { View } from '../types';

/**
 * Validates if a pair of coordinate strings represent valid latitude and longitude
 * @param lat - Latitude string
 * @param lng - Longitude string
 * @returns boolean indicating if coordinates are valid
 */
export const isValidCoordinate = (lat: string, lng: string): boolean => {
  // Check if both values exist and are not empty
  if (!lat || !lng || lat.trim() === '' || lng.trim() === '') {
    return false;
  }

  // Parse to numbers
  const latNum = parseFloat(lat);
  const lngNum = parseFloat(lng);

  // Check if parsing was successful (not NaN)
  if (isNaN(latNum) || isNaN(lngNum)) {
    return false;
  }

  // Check if coordinates are within valid ranges
  // Latitude: -90 to 90, Longitude: -180 to 180
  if (latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180) {
    return false;
  }

  return true;
};

/**
 * Converts coordinate strings to numbers if valid
 * @param lat - Latitude string
 * @param lng - Longitude string
 * @returns Coordinate object or null if invalid
 */
export const parseCoordinates = (lat: string, lng: string): { lat: number; lng: number } | null => {
  if (!isValidCoordinate(lat, lng)) {
    return null;
  }

  return {
    lat: parseFloat(lat),
    lng: parseFloat(lng)
  };
};

/**
 * Analyzes coordinate availability in a dataset of views
 * @param views - Array of views to analyze
 * @returns Statistics about coordinate coverage
 */
export const getCoordinateStats = (views: View[]) => {
  let totalLocations = 0;
  let withCoordinates = 0;
  let withoutCoordinates = 0;
  let viewsWithCompleteCoords = 0;
  let viewsWithPartialCoords = 0;
  let viewsWithNoCoords = 0;

  views.forEach(view => {
    const hasSourceCoords = isValidCoordinate(view.source_lat || '', view.source_long || '');
    const hasDestCoords = isValidCoordinate(view.dest_lat || '', view.dest_long || '');

    // Count total locations (source + destination)
    totalLocations += 2;

    // Count locations with coordinates
    if (hasSourceCoords) withCoordinates++;
    else withoutCoordinates++;

    if (hasDestCoords) withCoordinates++;
    else withoutCoordinates++;

    // Count views by coordinate completeness
    if (hasSourceCoords && hasDestCoords) {
      viewsWithCompleteCoords++;
    } else if (hasSourceCoords || hasDestCoords) {
      viewsWithPartialCoords++;
    } else {
      viewsWithNoCoords++;
    }
  });

  const coveragePercentage = totalLocations > 0 ? Math.round((withCoordinates / totalLocations) * 100) : 0;

  return {
    totalViews: views.length,
    totalLocations,
    withCoordinates,
    withoutCoordinates,
    coveragePercentage,
    viewsWithCompleteCoords,
    viewsWithPartialCoords,
    viewsWithNoCoords
  };
};

/**
 * Logs coordinate statistics to console for debugging
 * @param views - Array of views to analyze
 */
export const logCoordinateStats = (views: View[]): void => {
  const stats = getCoordinateStats(views);
  
  // Coordinate coverage analysis complete
}; 